import { getCookie } from "../../utils/cookie";
import { ShowContentDialog } from "./content-dialog";
import { expressEventListener, expressQuerySelector } from "../../common/html";
import { IPopup } from "./popup-new";
import { checkAccountValidityAsync } from "../../services/account-service";
import { IDisposable } from "../../utils/disposable";
import { isSafari } from "../../common/browser";

/* Info about the cookie store api https://github.com/WICG/cookie-store/blob/main/README.md */

export interface ILoginWatcher {
	readonly start: (force?: boolean) => void;
	readonly cancel: () => void;
	readonly stop: () => void;
}

export async function CreateAndStartLoginWatcher() {
	// Init Login-check for pages that require authentication
	let openDialog = false;
	const supportsCookieStoreApi = "cookieStore" in window;
	const handleCookieChanges = (
		emptyGuid: string,
		startingAccId: string,
		currentAccountId: string,
		currentAccountType: string,
		action: () => void,
	) => {
		if (currentAccountId === startingAccId) return; // return nothing to report

		// Already logged in on the auth page
		if (
			(window.context.pageName === "Login" || window.context.pageName === "Logout") &&
			currentAccountId != emptyGuid &&
			currentAccountType === "Full"
		) {
			action();
			location.reload();
			return;
		}

		// User logged out in another tab
		if (!currentAccountId || currentAccountId === emptyGuid) {
			alert("Label.LoginManager.LogoutInDifferentTab", "Label.LoginManager.LogoutInDifferentTab.PopupTitle");
			action();
			return;
		}

		// User is logged in with a different account.
		if (currentAccountType === "full") {
			// We used to be a FULL, now a different account -> Impossible
			alert("Label.LoginManager.DifferentAccount", "Label.LoginManager.DifferentAccount.PopupTitle");
			action();
			return;
		}

		// TEMP got merged into FULL -> OK
		// Different FULL -> NOK
		// We need a webservice call for this ...
		openDialog = true; // set this in case that the api call takes longer then the interval and it does it again
		checkAccountValidityAsync(startingAccId, currentAccountId).then((res) => {
			openDialog = false;
			if (!res.isValid) {
				alert("Label.LoginManager.DifferentAccount", "Label.LoginManager.DifferentAccount.PopupTitle");
				return;
			}

			startingAccId = currentAccountId;
		});
	};

	const alert = (key: string, title: string) => {
		let dialog: IPopup;
		if (openDialog) return;
		openDialog = true;
		const dialogEl = expressQuerySelector<HTMLElement>(document, ".has-open-dialog", false);
		if (dialogEl && dialog) dialog.close();

		const onOpen = (popupEl: HTMLElement) => {
			const okButton = expressQuerySelector<HTMLElement>(popupEl, ".technical-general-popup-ok-button", false);

			const closePopup = () => {
				closeEventPopup(dialog);
			};

			if (okButton) okButton.addEventListener("click", closePopup);
		};

		ShowContentDialog({
			dialogId: "information",
			label: key,
			title: title,
			extraPopupCss: "p-default",
			addTitleCssClass: false,
			okButtonKey: "Label.OkButtonText",
			openFunction: onOpen,
			onClose: () => location.reload(),
		}).then((popup) => {
			dialog = popup;
		});
	};

	const closeEventPopup = (dialog: IPopup) => {
		location.reload();
		openDialog = false;
		dialog.close();
	};

	if (window.context.pageRequiresAuth.toLowerCase() === "true") {
		const emptyGuid = "00000000-0000-0000-0000-000000000000";
		let startingAccId;

		const startOldLoginWatcher = () => {
			if (window.loginWatcher) window.loginWatcher.start();
			else {
				startingAccId = getCookie("accid") || emptyGuid;

				const createLoginWatcher = () => {
					let timerId = -1;
					let accountType;

					const start = (force?: boolean) => {
						clearInterval(timerId);
						if (force) openDialog = false;
						startingAccId = getCookie("accid") || emptyGuid;
						accountType = getCookie("acctype");

						// @ts-ignore
						timerId = setInterval(() => {
							run();
						}, 1500);
					};

					const run = () => {
						if (openDialog) return;

						const clear = () => {
							clearInterval(timerId);
						};
						return handleCookieChanges(
							emptyGuid,
							startingAccId,
							getCookie("accid") || emptyGuid,
							getCookie("acctype"),
							clear,
						);
					};

					const cancel = () => {
						openDialog = true; // so that it does not open while we try to cancel
						clearInterval(timerId);
					};

					const stop = () => {
						clearInterval(timerId);
					};

					return {
						start,
						cancel,
						stop,
					} as ILoginWatcher;
				};

				window.loginWatcher = createLoginWatcher();
				window.loginWatcher.start();
			}
		};

		if (supportsCookieStoreApi && cookieStore !== undefined && !isSafari()) {
			try {
				const startingAccIdCookie = await cookieStore.get("accid");
				startingAccId = startingAccIdCookie?.value || emptyGuid;
				if (window.loginWatcher) window.loginWatcher.start();
				const createCookieStoreWatcher = () => {
					let disposableEvent: IDisposable | null;

					const start = () => {
						disposableEvent = expressEventListener(cookieStore, "change", async (event) => {
							const accIdCookie = await cookieStore.get("accid");
							const accTypeCookie = await cookieStore.get("acctype");
							let currentAccountId = emptyGuid;
							if (accIdCookie) currentAccountId = accIdCookie.value;

							let currentAccountType = "";
							if (accTypeCookie) currentAccountType = accTypeCookie.value;

							for (const cookie of event.changed) {
								if (cookie.name === "accid") {
									currentAccountId = cookie.value;
								}
								if (cookie.name === "acctype") {
									currentAccountType = cookie.value;
								}
							}

							for (const cookie of event.deleted) {
								if (cookie.name === "accid") {
									currentAccountType = emptyGuid;
								}
								if (cookie.name === "acctype") {
									currentAccountType = "";
								}
							}

							const dispose = () => {
								disposableEvent && disposableEvent.dispose();
							};

							return handleCookieChanges(emptyGuid, startingAccId, currentAccountId, currentAccountType, dispose);
						});
					};

					const cancel = () => {
						if (disposableEvent) disposableEvent.dispose();
					};

					const stop = () => {
						cancel();
					};

					return {
						start,
						cancel,
						stop,
					} as ILoginWatcher;
				};
				window.loginWatcher = createCookieStoreWatcher();
				window.loginWatcher.start();
			} catch (e) {
				startOldLoginWatcher();
			}
		} else {
			startOldLoginWatcher();
		}
	}
}
